import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from '../../store';
import { viabilitySetpFourManager, viabilityManager, conditioningManager, ViabilityStudyHookType, optionsValueType, selectedStudyOptions } from '../../../@types/viability/viabilityStudy'
import { StepFourDataMock, StepFourDataInitial } from '../../../_mock/viabilityStudy'
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import { v4 as uuidv4 } from 'uuid';
import { post } from '../api/api';
import moment from 'moment'
import { editStudie } from '../dashboard/studies'
import { createErrorLog } from 'src/utils/log';

const db = firebase.firestore();

type ViabilityStudyState = {
  isLoading: boolean,
  isLoadingButton: boolean,
  isDownloadDWG: boolean,
  openConfirmDialog: boolean,
  error: Error | string | null,
  step: number,
  stepTwoData: any,
  stepThreeData: any | null,
  stepFourData: viabilitySetpFourManager,
  sendVersionStepThree: boolean,
  stepTwoConditionings: conditioningManager[],
  selectedConditioning: number | null,
  toRemove: number | null,
  toDisable: [],
  markerPosition: any,
  coord: any,
  image: string | null,
  object3D: any,
  generateVersion: boolean,
  flagVGV: boolean,
  kmzFileLink: string | null,
  dxfFileLink: string | null,
  otherFilesLink: any,
  kmzBody: any,
  studyId: string | null
};

const initialState: ViabilityStudyState = {
  isLoading: false,
  isLoadingButton: false,
  isDownloadDWG: false,
  openConfirmDialog: false,
  error: null,
  step: 0,
  stepTwoData: null,
  stepThreeData: null,
  stepFourData: StepFourDataInitial,
  stepTwoConditionings: [],
  selectedConditioning: -1,
  toRemove: null,
  toDisable: [],
  markerPosition: null,
  coord: null,
  image: null,
  object3D: null,
  sendVersionStepThree: false,
  generateVersion: false,
  flagVGV: false,
  kmzFileLink: null,
  dxfFileLink: null,
  otherFilesLink: null,
  kmzBody: null,
  studyId: null
};

const slice = createSlice({
  name: 'loading',
  initialState,
  reducers: {
    resetViabilityStudyState(state) {
      state = {
        isLoading: false,
        isLoadingButton: false,
        isDownloadDWG: false,
        openConfirmDialog: false,
        error: null,
        step: 1,
        stepTwoData: null,
        stepThreeData: null,
        stepFourData: StepFourDataInitial,
        stepTwoConditionings: [],
        selectedConditioning: -1,
        toRemove: null,
        toDisable: [],
        markerPosition: null,
        coord: null,
        image: null,
        object3D: null,
        sendVersionStepThree: false,
        generateVersion: false,
        flagVGV: false,
        kmzFileLink: null,
        dxfFileLink: null,
        otherFilesLink: null,
        kmzBody: null,
        studyId: null
      };
    },
    startLoading(state) {
      state.isLoading = true;
    },
    startLoadingButton(state) {
      state.isLoadingButton = true;
    },
    stopLoadingButton(state) {
      state.isLoadingButton = false;
    },
    setIsDownloadDWG(state, action) {
      state.isDownloadDWG = action.payload
    },
    setOpenConfirmDialog(state, action) {
      state.openConfirmDialog = action.payload;
    },
    addKMZFileLink(state, action) {
      state.kmzFileLink = action.payload;
    },
    addDxfFileLink(state, action) {
      state.dxfFileLink = action.payload;
    },
    addAuxFileLinks(state, action) {
      state.otherFilesLink = action.payload;
    },
    stopLoading(state) {
      state.isLoading = false;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    setStep(state, action) {
      state.step = action.payload;
      state.isLoading = false;
    },
    setMarkerPosition(state, action) {
      state.markerPosition = action.payload;
    },
    setCoord(state, action) {
      state.coord = action.payload;
    },
    setImage(state, action) {
      state.image = action.payload;
    },
    setObjectFor3D(state, action) {
      state.object3D = action.payload;
    },
    setSendVersionStepThree(state, action) {
      state.sendVersionStepThree = action.payload;
    },
    setStepFourData(state, action) {
      state.isLoading = false;
      state.stepFourData = action.payload;
    },
    setStepThreeData(state, action) {
      state.isLoading = false;
      state.stepThreeData = JSON.parse(JSON.stringify(action.payload));
    },
    setStepTwoData(state, action) {
      state.isLoading = false;
      state.stepTwoData = action.payload;
    },
    changeConditionShow(state, action) {
      state.stepTwoConditionings[action.payload.index].show = action.payload.show;
      state.toDisable = action.payload
    },
    addCondition(state, action) {
      state.stepTwoConditionings.push({
        color: action.payload.color,
        name: action.payload.title,
        type: action.payload.type,
        sizeReadable: '0m²',
        size: 0,
        show: false,
        layerId: null
      })
    },
    removeCondition(state, action) {
      state.stepTwoConditionings.splice(action.payload.index, 1)
      state.toRemove = action.payload.layerId
    },
    setSelectedConditioning(state, action) {
      state.selectedConditioning = action.payload;
    },
    setSizeConditioning(state, action) {
      state.stepTwoConditionings[action.payload.index].size = action.payload.size;
      state.stepTwoConditionings[action.payload.index].sizeReadable = action.payload.sizeReadable;
    },
    setLayerIdConditioning(state, action) {
      state.stepTwoConditionings[action.payload.index].layerId = action.payload.layerId;
      state.stepTwoConditionings[action.payload.index].show = true;
    },
    setGenerateVersion(state, action) {
      state.generateVersion = action.payload;
    },
    setFlagVGV(state, action) {
      state.flagVGV = action.payload;
    },
    updateVGV(state, action) {
      state.stepFourData.estimated_vgv = action.payload;
    },
    setkmzBody(state, action) {
      state.kmzBody = action.payload
    },
    setStudyId(state, action) {
      state.studyId = action.payload
    },
  },
});

export const { 
  setFlagVGV, 
  updateVGV, 
  startLoading, 
  stopLoading, 
  startLoadingButton, 
  stopLoadingButton, 
  setIsDownloadDWG, 
  setOpenConfirmDialog, 
  setStep, 
  setMarkerPosition, 
  setCoord, 
  setStepThreeData, 
  setStepTwoData, 
  changeConditionShow, 
  addCondition, 
  removeCondition, 
  setSelectedConditioning, 
  setLayerIdConditioning, 
  setSizeConditioning, 
  setImage, 
  setObjectFor3D, 
  setSendVersionStepThree, 
  setGenerateVersion, 
  setkmzBody, 
  setStudyId, 
  resetViabilityStudyState 
} = slice.actions;

export default slice.reducer;

export function sendStepThree(data: any, liquidArea: any) {
  return async () => {
    try {
      if (liquidArea) {
        dispatch(slice.actions.setStepFourData({ ...StepFourDataMock, occupation_data: { ...StepFourDataMock.occupation_data, liquid_area: liquidArea } }))
      } else {
        dispatch(slice.actions.setStepFourData(StepFourDataMock))
      }
      dispatch(slice.actions.setStepThreeData(data));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function sendDataVersionStepThree(data: viabilityManager, liquidArea: any) {
  return async () => {
    try {
      if (liquidArea) {
        dispatch(slice.actions.setStepFourData({ ...StepFourDataMock, occupation_data: { ...StepFourDataMock.occupation_data, liquid_area: liquidArea } }))
      } else {
        dispatch(slice.actions.setStepFourData(StepFourDataMock))
      }
      dispatch(slice.actions.setStepThreeData(data));
      dispatch(slice.actions.setSendVersionStepThree(false))
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function saveStudy(uid: any, stepThreeData: any, stepFourData: any, markerPosition: any, coord: any, img: any, object3D: any, nameAndVer: any, studyInfos: any) {
  return async () => {
    dispatch(slice.actions.startLoading());
    let dataHelper: any = {}
    try {
      var collId = uuidv4()
      const study = {
        id: collId,
        name: nameAndVer.name,
        area: studyInfos.area,
        perimeter: studyInfos.perimeter,
        image: img,
        entrance: markerPosition,
        coord: coord,
      }
      dataHelper = { id: uuidv4(), ...{ object3d: object3D }, ...{ image: img }, ...{ name: nameAndVer.versionName }, ...stepThreeData, ...stepFourData, lots: stepFourData.title.split(' ')[0] }
      await db.collection("studies").doc(uid).collection("data").doc(collId).set(study);
      await db.collection("studies").doc(uid).collection("data").doc(collId).collection('versions').add(dataHelper)

      dispatch(slice.actions.stopLoading());
      return true
    } catch (error) {
      console.error(error);
      return false
    }
  };
}

export function saveVersion(uid: any, stepThreeData: any, stepFourData: any, img: any, object3D: any, nameAndVer: any, collId: any) {
  return async () => {
    dispatch(slice.actions.startLoading());
    let dataHelper: any = {}
    try {
      dataHelper = { id: uuidv4(), ...{ object3d: object3D }, ...{ image: img }, ...{ name: nameAndVer.versionName }, ...stepThreeData, ...stepFourData, lots: stepFourData.title.split(' ')[0] }
      await db.collection("studies").doc(uid).collection("data").doc(collId).collection('versions').add(dataHelper)
      dispatch(slice.actions.stopLoading());
      return true
    } catch (error) {
      console.error(error);
      return false
    }
  };
}

export function saveKMZ(data: any) {
  return async () => {
    try {
      const response = await post('/order/save-kmz-file', data);
      dispatch(slice.actions.addKMZFileLink(response.data));
      if(response?.data){
        return response.data;
      } else {
        return null;
      }
    } catch (error) {
      console.error(error);
      createErrorLog(`Erro no método saveKMZ: ${JSON.stringify(error)}`)
      return error;
    }
  }
}

export function createDXF(data: any) {
  return async () => {
    try {
      const response = await post('/files/downloadKmzAsDxf', data);
      dispatch(slice.actions.addDxfFileLink(response.data));
      if(response?.data){
        return response.data;
      } else {
        return null;
      }
    } catch (error) {
      console.error(error);
      createErrorLog(`Erro no método createDWG: ${JSON.stringify(error)}`)
      return error;
    }
  }
}

export async function saveAuxFiles(data: any) {
  try {
    const response = await post('/order/save-aux-files', data);
    return response?.data;
  } catch (error) {
    console.error(error);
    createErrorLog(`Erro no método saveAuxFiles: ${JSON.stringify(error)}`)
    return error;
  }
}

export function saveKMZstudy(data: any) {
  return async () => {
    try {
      const response = await post('/order/save-kmz-file-study', data);
      return response?.data;
    } catch (error) {
      console.error(error);
      createErrorLog(`Erro no método saveKMZstudy: ${JSON.stringify(error)}`)
      return null;
    }
  }
}

export function newSaveStudy(userName: any, address: any, kmzFileLink: string, viabilityStudyHook: ViabilityStudyHookType, uid: any, stepThreeData: any, stepFourData: any, markerPosition: any, coord: any, img: string, infosFirstModal: any, studyInfos: any, polarCoords: any, selectedOptions: selectedStudyOptions, optionsValue: optionsValueType, status: string) {
  return async () => {
    try {
      const auxBody = await viabilityStudyHook.getFilesData();
      const otherFilesLink = await saveAuxFiles(auxBody);
      const collId = uuidv4();
      const study = {
        id: collId,
        urbia_label: 'awaiting_result',
        kmzFileLink: kmzFileLink ?? '',
        name: infosFirstModal?.name ?? '',
        description: infosFirstModal?.description ?? '',
        address: address ?? '',
        area: studyInfos?.area ?? '',
        perimeter: studyInfos?.perimeter ?? '',
        image: img ?? '',
        dataHelper: {
          id: collId,
          ...{ image: img },
          ...stepThreeData,
          ...polarCoords,
          ...stepFourData,
          lots: stepFourData.title.split(' ')[0],
          enterprise_standard: infosFirstModal?.enterprise_standard ?? '',
          // gleba_size_ha: infosFirstModal?.gleba_size_ha ?? '',
          legislation_info: infosFirstModal?.legislation_info ?? '',
          otherFilesLink: otherFilesLink,
        },
        entrance: markerPosition ?? '',
        coord: coord ?? '',
        closedCon: infosFirstModal?.closedCon ?? '',
        housesCon: infosFirstModal?.housesCon ?? '',
        openLot: infosFirstModal?.openLot ?? '',
        controledLot: infosFirstModal?.controledLot ?? '',
        other: infosFirstModal?.other_product ?? '',
        createdAt: moment().format('DD/MM/YYYY'),
        userName: userName,
        userId: uid,
        selectedOptions: selectedOptions,
        optionsValue: optionsValue,
        status: status
      };
      await db.collection("studies").doc(uid).set({ userId: uid });
      await db.collection("studies").doc(uid).collection("data").doc(collId).set(study);
      return collId;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };
}

export function editPaymentStatus(study: any, status: any) {
  return async () => {
    try {
      var aux = JSON.parse(JSON.stringify(study))
      aux.paymentStatus = status
      await db.collection('studies').doc(aux.userId).collection('data').doc(aux.id).set(aux)
    } catch (error) {
      console.error(error);
    }
  }
}

export function editStudyUploadFile(study: any, fileUrl: any) {
  return async () => {
    try {
      var aux = JSON.parse(JSON.stringify(study))
      aux.urbia_label = "available_result"
      if (aux.results) {
        aux.results.push({ link: fileUrl, date: moment().format('DD/MM/YYYY') })
      } else {
        aux.results = [{ link: fileUrl, date: moment().format('DD/MM/YYYY') }]
      }
      await db.collection('studies').doc(aux.userId).collection('data').doc(aux.id).set(aux)
      await dispatch(editStudie(aux))
    } catch (error) {
      console.error(error);
    }
  }
}

export function editStudyDeleteFile(study: any, file: any) {
  return async () => {
    try {
      var aux = JSON.parse(JSON.stringify(study))
      if (aux.results.length === 1) {
        aux.results = []
        aux.urbia_label = "awaiting_result"
      } else {
        const newResult: any = []
        aux.results.forEach((result: any) => {
          if (result.link !== file.link) {
            newResult.push(result);
          }
        });
        aux.results = newResult
      }
      await db.collection('studies').doc(aux.userId).collection('data').doc(aux.id).set(aux)
      await dispatch(editStudie(aux))
    } catch (error) {
      console.error(error);
    }
  }
}

export function updateSelectedOptions(userId: any, studyId: any, newOptions: any) {
  return async () => {
    try {
      if(studyId && userId){
        await db.collection('studies').doc(userId.uid).collection('data').doc(studyId).update({
          selectedOptions: newOptions
        });
      }
    } catch (error) {
      throw error;
    }
  };
}